import Swal, { SweetAlertIcon } from 'sweetalert2'
import iziToast, { IziToastSettings } from 'izitoast'

export interface JSMessage {
    type: string
    title: string
    text: string
    showCancelButton?: boolean
    confirmButtonText?: string
    cancelButtonText?: string
    successFunction?: string
}

export interface JSMessageResponse {
    message?: JSMessage
    [key: string]: any
}

/**
 * Handles legacy JSMessage responses from the backend.
 * Uses iziToast for simple notifications and SweetAlert2 for interactive ones.
 */
export const handleJSMessage = (data: unknown): boolean => {
    if (!data || typeof data !== 'object') return false

    let message = (data as JSMessageResponse).message

    // Support for normalized API responses where message is inside data
    if (!message && (data as any).data?.message) {
        message = (data as any).data.message
    }

    // Support for legacy error structure: {"error_code": 0, "error_message": "..."}
    if (!message && typeof (data as any).error_message === 'string') {
        message = {
            type: 'error',
            title: 'Ошибка',
            text: (data as any).error_message
        }
    }

    if (!message || typeof message !== 'object') return false

    const type = message.type || 'info'

    // Decide if we need a modal (interactive) or a toast (simple)
    const isInteractive = message.showCancelButton || message.successFunction

    if (isInteractive) {
        const iconMap: Record<string, SweetAlertIcon> = {
            success: 'success',
            error: 'error',
            warning: 'warning',
            info: 'info',
        }
        const icon = iconMap[type] || 'info'

        Swal.fire({
            title: message.title || (type === 'error' ? 'Ошибка' : 'Информация'),
            html: message.text || '',
            icon,
            showCancelButton: message.showCancelButton || false,
            confirmButtonText: message.confirmButtonText || 'OK',
            cancelButtonText: message.cancelButtonText || 'Отмена',
            confirmButtonColor: type === 'error' ? '#DD6B55' : '#532de0',
        })
    } else {
        // Use iziToast for simple status messages
        const iziOptions: IziToastSettings = {
            title: message.title,
            message: message.text,
            position: 'topRight',
            timeout: type === 'error' ? 8000 : 5000,
            close: true,
            displayMode: 1, // once
            transitionIn: 'fadeInDown',
            transitionOut: 'fadeOutUp',
            progressBar: true,
            progressBarColor: type === 'error' ? '#EB5757' : (type === 'success' ? '#27AE60' : '#532DE0'),
            backgroundColor: 'transparent',
        }

        switch (type) {
            case 'success':
                iziToast.success(iziOptions)
                break
            case 'error':
                iziToast.error(iziOptions)
                break
            case 'warning':
                iziToast.warning(iziOptions)
                break
            default:
                iziToast.info(iziOptions)
        }
    }

    return true
}
